import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../orpc'
import type { AuditLogEntry } from '../audit-format'

export type AuditLogCursor = { createdAt: string; id: string }

// The server returns richer rows (ip, requestId, ...); this is the subset the
// UI consumes.
export type AuditLogPage = {
  items: AuditLogEntry[]
  nextCursor: AuditLogCursor | null
}

const PAGE_SIZE = 50

// Admin-only activity feed, newest first. Keyset-paginated: each page hands
// back a { createdAt, id } cursor for the next one. Returns the query plus
// the pages flattened into a single `entries` list.
export function useAuditLogList(filters: { entityType?: string } = {}) {
  const orpc = useOrpcTanstackQueryUtils()
  const query = useInfiniteQuery(
    orpc.audit.list.infiniteOptions({
      input: (pageParam: AuditLogCursor | null) => ({
        ...filters,
        cursor: pageParam,
        limit: PAGE_SIZE,
      }),
      initialPageParam: null as AuditLogCursor | null,
      getNextPageParam: (lastPage: AuditLogPage) => lastPage.nextCursor ?? undefined,
    })
  )

  const data = query.data as InfiniteData<AuditLogPage> | undefined
  return { ...query, entries: (data?.pages ?? []).flatMap((page) => page.items) }
}

// Distinct entity types recorded for this company — drives the filter chips.
export function useAuditLogEntityTypes() {
  const orpc = useOrpcTanstackQueryUtils()
  const query = useQuery(orpc.audit.getEntityTypes.queryOptions())
  return { ...query, entityTypes: (query.data ?? []) as string[] }
}
